import { createStyles, makeStyles } from "@material-ui/core";
import * as React from "react";

import {
    MarketResearchError,
    MarketResearchSupplyDemandSkeleton,
    MarketResearchSupplyDemandTopEmployer,
    useMarketResearchContext,
} from "..";
import {
    Bar,
    baseColors,
    Breakpoint,
    CandidateIcon,
    DemandVersusTimeGraph,
    EnhancedTable,
    RelodeTheme,
    Text,
    useDeviceSize,
} from "../../..";

const useMarketResearchSupplyDemandStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            section: {
                display: "flex",
                flexDirection: "column",
                marginBottom: theme.spacing(10),
            },
            mobileSection: {
                display: "flex",
                flexDirection: "column",
                marginBottom: theme.spacing(4),
            },
            header: {
                padding: theme.spacing(6, 1),
                backgroundColor: baseColors.warm_gray,
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                columnGap: theme.spacing(3),
                alignItems: "center",
                marginBottom: theme.spacing(10),
            },
            mobileHeader: {
                padding: theme.spacing(2, 1),
                backgroundColor: baseColors.warm_gray,
                alignItems: "center",
                marginBottom: theme.spacing(4),
            },
            icon: {
                marginLeft: theme.spacing(4),
            },
        }),
    { name: "MarketResearchSupplyDemand" }
);

export const MarketResearchSupplyDemand = () => {
    const {
        supplyDemand: { data, isBusy, error },
    } = useMarketResearchContext();

    const device = useDeviceSize();
    const classes = useMarketResearchSupplyDemandStyles();

    if (isBusy) {
        return <MarketResearchSupplyDemandSkeleton />;
    }
    if (error) {
        return <MarketResearchError title="supply & demand" error={error} />;
    }
    if (data === undefined) return null;

    const maxCompanyCount = Math.max(
        ...(data?.topEmployers?.map(p => p.demandCount) ?? [])
    );

    const SupplyDemandHeader = () => {
        const hasSupplyDemandData =
            !!data?.summary?.supplyCount && !!data?.summary?.demandCount;
        return (
            <div
                className={
                    device === "mobile" ? classes.mobileHeader : classes.header
                }
            >
                <Breakpoint desktop>
                    <div className={classes.icon}>
                        <CandidateIcon fontSize="large" color="primary" />
                    </div>
                </Breakpoint>
                {hasSupplyDemandData ? (
                    <SupplyDemandSummaryText />
                ) : (
                    <SupplyDemandSummaryNoDataText />
                )}
            </div>
        );
    };

    const SupplyDemandSummaryText = () => (
        <Text variant="h2">
            <span>There are </span>
            <span style={{ color: baseColors.magenta }}>
                {data?.summary?.supplyCount?.toLocaleString()}
            </span>
            <span> profiles and </span>
            <span style={{ color: baseColors.magenta }}>
                {data?.summary?.demandCount?.toLocaleString()}
            </span>
            <span> job postings, </span>
            {data?.summary?.rating.isNegative()
                ? "giving candidates an advantage."
                : "giving employers an advantage."}
        </Text>
    );

    const SupplyDemandSummaryNoDataText = () => (
        <Text variant="h2">
            <span>
                Insufficient data available to determine hiring advantage.
            </span>
        </Text>
    );

    const ActiveCompaniesSection = () => (
        <>
            <Text variant="h3">active companies</Text>
            <EnhancedTable
                columns={[
                    {
                        field: "name",
                        title: "company",
                        columnSize: "larger",
                    },
                    {
                        field: "count",
                        title: "demand count",
                        columnSize: "small",
                        align: "right",
                        render: (
                            employer: MarketResearchSupplyDemandTopEmployer
                        ) => employer.demandCount.toLocaleString(),
                    },
                    {
                        field: "count",
                        columnSize: "fill",
                        title: null,
                        hidden: device === "mobile",
                        render: (
                            employer: MarketResearchSupplyDemandTopEmployer
                        ) => {
                            const pct =
                                (employer.demandCount / maxCompanyCount) * 100;
                            return (
                                <Bar
                                    color={baseColors.light_blue}
                                    percent={pct}
                                />
                            );
                        },
                    },
                ]}
                data={data?.topEmployers}
                options={{
                    hideCheckbox: true,
                }}
                dataCy={"marketResearchSupplyDemand-topCompanies"}
            />
        </>
    );

    return (
        <div style={{ flexGrow: 1 }}>
            <SupplyDemandHeader />
            <div
                className={
                    device === "mobile"
                        ? classes.mobileSection
                        : classes.section
                }
            >
                <Text variant="h3">demand trend - new job postings</Text>
                <DemandVersusTimeGraph
                    mobile={device === "mobile"}
                    data={data?.demandTrends}
                />
            </div>
            {data?.topEmployers?.length > 0 ? (
                <div
                    className={
                        device === "mobile"
                            ? classes.mobileSection
                            : classes.section
                    }
                >
                    <ActiveCompaniesSection />
                </div>
            ) : null}
        </div>
    );
};
